import { Suspense, useEffect, useRef } from "react";
import {
  SidebarProvider,
  SidebarInset,
  SidebarTrigger,
} from "@/frontend/components/ui/sidebar";
import { Outlet, useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { AppSidebar } from "./AppSidebar";
import { useSubscriptionStore } from "@/frontend/stores/subscriptionStore";
import { useMemberStore } from "@/frontend/stores/memberStore";
import { useOrgSettingsStore } from "@/frontend/stores/orgSettingsStore";
import { useOrgStore } from "@/frontend/stores/orgStore";
import { IoWarningOutline } from "react-icons/io5";
import { ChevronDown } from "lucide-react";
import { setActiveOrganization } from "@/frontend/lib/switch-organization";
import { PushNotificationReconciler } from "./PushNotificationReconciler";
import { WhatsAppConnectionBanner } from "./WhatsAppConnectionBanner";
import { DailyCapBanner } from "./DailyCapBanner";
import { ProductTourHost } from "./ProductTour";
import { FirstWinDialog } from "./onboarding/FirstWinDialog";
import { CallBar } from "./calls/CallBar";
import { useDailyCapStore } from "@/frontend/stores/dailyCapStore";
import { shouldHideWahaUiForOrg } from "@/frontend/lib/meta-review";
import {
  useChannelHealth,
  type DisconnectedChannel,
} from "@/frontend/hooks/useDashboard";
import { getOrganizationBillingPath } from "@/shared/billing-path";
import { useSession } from "@/frontend/lib/auth";
import type { Feature } from "@/shared/rbac";

const landingPages: Array<[Feature, string]> = [
  ["dashboard", "dashboard"], ["inbox", "inbox"], ["calls", "calls"], ["chat", "chat"],
  ["alerts", "alerts"], ["flows", "jobs"], ["campaigns", "campaigns"],
  ["autopilot", "ai-agent"], ["pulse", "pulse"], ["tasks", "tasks"],
  ["sales", "sales"],
  ["contacts", "contacts"], ["templates", "templates"],
  ["profile", "settings/profile"], ["notifications", "settings/notifications"],
  ["apiKeys", "settings/api-keys"], ["teamSettings", "settings/users"],
  ["billing", "settings/billing"], ["integrations", "settings/integrations"],
  ["channels", "settings/channels"], ["whatsappBusiness", "settings/whatsapp-business"],
  ["instagram", "settings/instagram"], ["websiteWidget", "settings/website-widget"],
  ["webAgent", "settings/web-agent"], ["labels", "settings/labels"],
  ["eventDefinitions", "settings/events"], ["profileAttributes", "settings/profile-attributes"],
  ["knowledgeBases", "settings/knowledge-bases"],
];

const RouteLoader = () => (
  <div className="flex min-h-0 flex-1 items-center justify-center" role="status">
    <span className="text-sm text-muted-foreground">Loading…</span>
  </div>
);

function getChannelHealthPreview(search: string): DisconnectedChannel[] {
  if (!import.meta.env.DEV) return [];
  const params = new URLSearchParams(search);
  const preview = params.get("previewChannelDisconnect");
  const incident = params.get("previewChannelIncident") || "1";
  const disconnectedAt = "2099-01-01T00:00:00.000Z";
  const channels: Record<string, DisconnectedChannel> = {
    whatsapp: {
      id: `preview-whatsapp-${incident}`,
      type: "whatsapp",
      label: "WhatsApp",
      name: "Connection owner",
      identifier: "919850879900",
      settingsSection: "whatsapp-cloud",
      disconnectedAt,
    },
    waba: {
      id: `preview-waba-${incident}`,
      type: "waba",
      label: "WhatsApp Business",
      name: "Querygen Support",
      identifier: "919850879900",
      settingsSection: "whatsapp-business",
      disconnectedAt,
    },
    instagram: {
      id: `preview-instagram-${incident}`,
      type: "instagram",
      label: "Instagram",
      name: "@querygen",
      identifier: null,
      settingsSection: "instagram",
      disconnectedAt,
    },
  };
  if (preview === "multiple") return [channels.waba, channels.instagram];
  return preview && channels[preview] ? [channels[preview]] : [];
}

const CreditsBanner = ({
  balance,
  isLowBalance,
  collapseOnMobile = false,
}: {
  balance: number;
  isLowBalance: boolean;
  collapseOnMobile?: boolean;
}) => {
  const { orgId } = useParams<{ orgId: string }>();
  if (!orgId) return null;

  const message = balance <= 0
    ? "Your workspace has used all included credits for this cycle."
    : `Your workspace is running low on included credits for this cycle (${balance} left).`;
  const action = isLowBalance ? "Update billing" : "Review plan";

  const content = (
    <>
      {message}{" "}
      <Link
        to={getOrganizationBillingPath(orgId)}
        className="font-semibold underline underline-offset-2 hover:text-amber-950 dark:hover:text-amber-100"
      >
        {action}
      </Link>{" "}
      to avoid unexpected overages.
    </>
  );

  return (
    <>
      <div className={`${collapseOnMobile ? "hidden md:block" : ""} border-b border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-950/50`}>
        <div className="flex items-center justify-center gap-2 text-amber-800 dark:text-amber-200">
          <IoWarningOutline className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm font-medium">{content}</span>
        </div>
      </div>
      {collapseOnMobile && (
        <details className="group border-b border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/70 dark:bg-amber-950/30 dark:text-amber-100 md:hidden">
          <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 px-4 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-600">
            <IoWarningOutline className="h-4 w-4 shrink-0" />
            <span className="flex-1">1 more issue needs attention</span>
            <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
          </summary>
          <div className="border-t border-amber-200 px-4 py-3 text-sm leading-5 text-amber-800 dark:border-amber-900/70 dark:text-amber-200">
            {content}
          </div>
        </details>
      )}
    </>
  );
};

export const AppLayout = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const { data: session } = useSession();
  const activeOrganizationIdRef = useRef(session?.session.activeOrganizationId);
  activeOrganizationIdRef.current = session?.session.activeOrganizationId;
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const isMobileConversationView = /\/app\/inbox\/[^/]+$/.test(pathname);
  const { setCurrentOrg } = useOrgStore();
  const {
    balance,
    hasCredits,
    isLowBalance,
    hasActiveSubscription,
    isLoading: subLoading,
    lastFetchedOrgId,
    fetchStatus,
  } = useSubscriptionStore();
  const dailyCapStatus = useDailyCapStore((state) => state.orgId === orgId ? state.status : null);
  const {
    fetchRole,
    reset: resetMemberStore,
    canRead,
    canWrite,
    fetched: memberFetched,
    permissions,
  } = useMemberStore();

  useEffect(() => {
    if (!orgId || !memberFetched || !permissions || pathname !== `/org/${orgId}/app/dashboard` || canRead("dashboard")) return;
    const destination = landingPages.find(([feature]) => canRead(feature))?.[1];
    navigate(destination ? `/org/${orgId}/app/${destination}` : "/docs/team-roles", { replace: true });
  }, [orgId, memberFetched, permissions, pathname, canRead, navigate]);
  const { fetchSettings: fetchOrgSettings, reset: resetOrgSettings } = useOrgSettingsStore();
  const { data: channelHealth } = useChannelHealth(
    memberFetched && canRead("channels") ? orgId : undefined,
  );

  // Ensure the session's activeOrganizationId matches the URL orgId.
  // This fixes cases where a new session is created without an active org
  // (e.g., token refresh, page reload), which would cause getActiveMemberRole()
  // to fail and treat admins as regular members.
  useEffect(() => {
    if (!orgId) return;

    let cancelled = false;
    const previousOrgId = useOrgStore.getState().currentOrgId;
    const organizationChanged = previousOrgId !== null && previousOrgId !== orgId;

    if (organizationChanged) {
      resetMemberStore();
      resetOrgSettings();
    }

    const syncActiveOrg = async () => {
      try {
        if (activeOrganizationIdRef.current !== orgId) {
          await setActiveOrganization(orgId);
        }

        if (cancelled) return;

        setCurrentOrg(orgId);
        await fetchRole(organizationChanged);
        if ((["organizationSettings", "labels", "sales", "inbox", "autopilot", "billing", "websiteWidget"] as const)
          .some((feature) => useMemberStore.getState().canRead(feature))) {
          await fetchOrgSettings();
        }
      } catch (error) {
        console.error("Failed to sync active organization:", error);
      }
    };

    syncActiveOrg();

    return () => {
      cancelled = true;
    };
  }, [orgId, setCurrentOrg, resetMemberStore, resetOrgSettings, fetchRole, fetchOrgSettings]);

  useEffect(() => {
    if (!orgId) return;
    let lastRefreshAt = Date.now();
    const refreshPermissions = () => {
      if (document.visibilityState !== "visible" || Date.now() - lastRefreshAt < 60_000) return;
      lastRefreshAt = Date.now();
      void fetchRole(true);
    };
    window.addEventListener("focus", refreshPermissions);
    document.addEventListener("visibilitychange", refreshPermissions);
    const interval = window.setInterval(refreshPermissions, 60_000);
    return () => {
      window.removeEventListener("focus", refreshPermissions);
      document.removeEventListener("visibilitychange", refreshPermissions);
      window.clearInterval(interval);
    };
  }, [orgId, fetchRole]);

  useEffect(() => {
    if (orgId && memberFetched && canRead("billing") && pathname !== getOrganizationBillingPath(orgId)) {
      fetchStatus(orgId);
    }
  }, [orgId, pathname, fetchStatus, memberFetched, canRead]);

  const previewChannels = getChannelHealthPreview(search);
  const scopedHealthChannels = channelHealth?.organizationId === orgId
    ? channelHealth.channels
    : [];
  const disconnectedChannels = (previewChannels.length > 0
    ? previewChannels
    : scopedHealthChannels).filter(
    (channel) => channel.type !== "whatsapp" || !shouldHideWahaUiForOrg(orgId),
  );
  const showCreditsBanner = !subLoading &&
    lastFetchedOrgId === orgId &&
    hasActiveSubscription &&
    (!hasCredits || isLowBalance);
  const showChannelBanner = Boolean(orgId && disconnectedChannels.length > 0);
  const showDailyCapBanner = Boolean(dailyCapStatus?.pausedAt);

  return (
    <SidebarProvider defaultOpen={true}>
      {canWrite("notifications") && <PushNotificationReconciler organizationId={orgId ?? null} />}
      <AppSidebar />
      <SidebarInset className="h-svh min-h-0 min-w-0 overflow-hidden md:ml-[256px] md:peer-data-[state=collapsed]:ml-0 transition-[margin] duration-200 ease-linear">
        {showChannelBanner && orgId && (
          <WhatsAppConnectionBanner
            orgId={orgId}
            channels={disconnectedChannels}
            isPreview={previewChannels.length > 0}
          />
        )}
        {orgId && memberFetched && canRead("campaigns") && (
          <DailyCapBanner orgId={orgId} collapseOnMobile={showChannelBanner} />
        )}
        {showCreditsBanner && (
          <CreditsBanner
            balance={balance}
            isLowBalance={isLowBalance}
            collapseOnMobile={showChannelBanner || showDailyCapBanner}
          />
        )}
        <CallBar userId={session?.user?.id} />
        {/* Mobile-only header with menu trigger */}
        {!isMobileConversationView && (
          <header className="md:hidden sticky top-0 z-40 bg-background border-b border-border px-4 py-3">
            <div className="flex items-center gap-2">
              <SidebarTrigger />
              <span className="text-sm font-medium text-muted-foreground">Menu</span>
            </div>
          </header>
        )}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto">
          <Suspense fallback={<RouteLoader />}>
            <Outlet />
          </Suspense>
        </div>
      </SidebarInset>
      <FirstWinDialog />
      <ProductTourHost />
    </SidebarProvider>
  );
};
